import { Check } from "lucide-react";
import { cn, formatCategoryDisplay } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import type { Tag, TagGroup } from "./types";
import { TAG_GROUP_COLORS } from "./types";

export interface TagCheckboxListProps {
  /** The tag group whose values are listed */
  group: TagGroup;
  /** Filter IDs currently selected within this group */
  selectedTags: string[];
  /** Toggle one tag (parent tags toggle their children — see the caller's hook) */
  onTagToggle: (tagId: string) => void;
  /** Placeholder for the search box */
  searchPlaceholder: string;
  /** Copy for "nothing matched the search" */
  emptyLabel: string;
  /** Show the per-value document count (default: true) */
  showItemCounts?: boolean;
  /** Controlled search value — omit to let cmdk own it */
  search?: string;
  onSearchChange?: (value: string) => void;
}

/** What the row actually renders — Drawing-prefixed categories show trimmed. */
function displayLabel(group: TagGroup, tag: Tag): string {
  return group.id === "categories" ? formatCategoryDisplay(tag.label) : tag.label;
}

/**
 * Match on what the user can SEE. cmdk's default scorer reads the item's
 * `value`, which is the tag id here (ids must be unique — see below), so search
 * is driven off `keywords` instead. A case-insensitive substring on the visible
 * label is the right behaviour for a filter list: predictable, and it never
 * matches an id fragment the user cannot see.
 */
function filterByKeywords(_value: string, search: string, keywords?: string[]): number {
  const needle = search.trim().toLowerCase();
  if (!needle) return 1;
  return keywords?.some((k) => k.toLowerCase().includes(needle)) ? 1 : 0;
}

/**
 * The searchable checkbox list of one dimension's values, shared by the
 * documents Filters popover (inline, as its second step) and by
 * TeachFranklinModal's TagDropdown (inside its own popover).
 *
 * `value={tag.id}` is load-bearing, not cosmetic. cmdk decides which row is
 * highlighted by comparing VALUE STRINGS against the single selected value in
 * its store, so two rows sharing a value both light up on hover and both report
 * `aria-selected`. Filter groups routinely produce that: a group and its only
 * item often carry the same name (useTagGroups now collapses the exactly-one
 * case, but a group with a same-named item PLUS others still renders both).
 * Ids are unique by construction — `filter-group-{id}`,
 * `filter-merged-{groupId}-{name}`, `category-{name}`, `construction-{id}`,
 * `stage-{id}` — so keying on them keeps one row highlighted at a time.
 */
export function TagCheckboxList(props: TagCheckboxListProps) {
  // Destructured in the body, not the signature: a props bag is one argument,
  // and signature-destructuring nine of them reads to static analysis as a
  // nine-parameter function.
  const {
    group,
    selectedTags,
    onTagToggle,
    searchPlaceholder,
    emptyLabel,
    showItemCounts = true,
    search,
    onSearchChange,
  } = props;
  const colors = TAG_GROUP_COLORS[group.color];

  return (
    <Command shouldFilter={true} filter={filterByKeywords}>
      <CommandInput placeholder={searchPlaceholder} value={search} onValueChange={onSearchChange} />
      <CommandList className="max-h-[300px] overflow-y-auto">
        <CommandEmpty>{emptyLabel}</CommandEmpty>
        <CommandGroup>
          {group.tags.map((tag) => {
            // Parent tags with children: checked when ANY child is selected.
            const isSelected = tag.childTagIds?.length
              ? tag.childTagIds.some((childId) => selectedTags.includes(childId))
              : selectedTags.includes(tag.id);
            const isSubcategory = tag.isSubcategory === true;
            const label = displayLabel(group, tag);
            return (
              <CommandItem
                key={tag.id}
                value={tag.id}
                keywords={[label]}
                onSelect={() => onTagToggle(tag.id)}
                className={cn(
                  "flex items-center gap-2",
                  isSubcategory && "pl-6" // Indent subcategories
                )}
              >
                <div
                  className={cn(
                    "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
                    isSelected
                      ? [colors.indicator, "border-transparent text-white"]
                      : "border-muted-foreground/50 opacity-50"
                  )}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                </div>
                <span className="flex-1 truncate">{label}</span>
                {showItemCounts && (
                  <span className="text-xs text-muted-foreground">({tag.count})</span>
                )}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
